import { Route, Routes } from 'react-router-dom';
import {
  adminRoutesConfig,
  customerRoutesConfig,
  publicAuthRoutesConfig,
} from './routesConfig';
import ProtectedRoute from './components/common/ProtectedRoute/ProtectedRoute';
import AlreadySignedInGuard from './components/common/AlreadySignedInGuard/AlreadySignedInGuard';
import PersonaRoute from './components/common/PersonaRoute/PersonaRoute';
import { AuthProviderOutlet } from './contexts/AuthContext';
import AuthorizedRoute from './components/common/AuthorizedRoute/AuthorizedRoute';
import AdminLayout from './components/Admin/layout';
import CustomerLayout from './components/Guest/layout';
import { ErrorPage } from './components/error/ErrorPage';

function renderRoutes(routes, persona) {
  return routes.map((route, idx) => {
    const Element = route.element;
    const props = {
      ...(route.extraProps || {}),
      ...(persona ? { persona } : {}),
    };
    return (
      <Route key={idx} path={route.path} element={<Element {...props} />} />
    );
  });
}

/**
 * Simple route hierarchy using existing tools:
 *
 * 1. Public routes first (always accessible)
 * 2. Error routes (always accessible)
 * 3. All other routes require authentication (redirect to /login if unauthenticated)
 *
 * UX Flow:
 * - /login, /customer/register (public) → Always accessible
 * - /error/404 (error) → Always accessible
 * - /admin/users, /customer/dashboard (protected, unauthenticated) → Redirect to /login
 * - /randomurl (unknown, unauthenticated) → Redirect to /login
 * - /randomurl (unknown, authenticated) → Show 404
 */
const AppRoutes = () => (
  <Routes>
    {/* AuthContext wraps EVERYTHING - single source of truth */}
    <Route element={<AuthProviderOutlet />}>
      {/* PUBLIC ROUTES - Always accessible, no auth required */}
      <Route element={<AlreadySignedInGuard />}>
        {renderRoutes(publicAuthRoutesConfig)}
      </Route>

      {/* ERROR ROUTES - Always accessible */}
      <Route path="/error/404" element={<ErrorPage statusCode={404} />} />

      {/* ALL OTHER ROUTES - Require authentication (including unknown routes) */}
      <Route element={<ProtectedRoute />}>
        {/* Admin persona — internal users only, with RBAC */}
        <Route element={<PersonaRoute persona="admin" />}>
          <Route element={<AuthorizedRoute />}>
            <Route element={<AdminLayout />}>
              {renderRoutes(adminRoutesConfig, 'admin')}
            </Route>
          </Route>
        </Route>

        {/* Customer persona — external users only */}
        <Route element={<PersonaRoute persona="customer" />}>
          <Route element={<CustomerLayout />}>
            {renderRoutes(customerRoutesConfig)}
          </Route>
        </Route>

        {/* CATCH-ALL inside ProtectedRoute - unknown routes redirect to login if not authenticated */}
        <Route path="*" element={<ErrorPage statusCode={404} />} />
      </Route>
    </Route>
  </Routes>
);

export default AppRoutes;
